// Canonical FSAP pricing + service data. Reused by web pages, PDFs, and analytics.
// Edit values here to change every FSAP surface at once.

export type PremiumTier = {
  id: "foundation" | "priority" | "elite";
  name: string;
  monthly: number;
  /** Stripe price ID (recurring, monthly) used by /book checkout. */
  priceId: string;
  tagline: string;
  bestFor: string;
  features: string[];
};

export const PREMIUM_TIERS: PremiumTier[] = [
  {
    id: "foundation",
    name: "Foundation FSAP",
    monthly: 1250,
    priceId: "fsap_foundation_monthly",
    tagline: "Monthly guidance and one meeting in the room.",
    bestFor: "Families managing an active IEP/504 with one meeting expected per month.",
    features: [
      "1 ARD/IEP or agency meeting per month (local, up to 3 hrs)",
      "2 strategy calls per month (30 min each)",
      "Email lane, 2 business-day response",
      "Document review up to 15 pages/month",
      "Written meeting prep + post-meeting recap",
    ],
  },
  {
    id: "priority",
    name: "Priority FSAP",
    monthly: 2750,
    priceId: "fsap_priority_monthly",
    tagline: "Active representation, faster access, deeper coordination.",
    bestFor: "Active disputes, compliance concerns, or cross-agency coordination.",
    features: [
      "2 meetings per month (ARD/IEP, agency, provider)",
      "Weekly 30-min strategy call",
      "48-hour email/phone response window",
      "Compliance review + records tracking",
      "Priority scheduling on escalation",
      "Written advocacy plan updated quarterly",
    ],
  },
  {
    id: "elite",
    name: "Elite FSAP",
    monthly: 5500,
    priceId: "fsap_elite_monthly",
    tagline: "Unlimited local meetings, on-call access, travel-ready.",
    bestFor: "High-stakes cases, multi-system navigation, or travel-required engagements.",
    features: [
      "Unlimited local meetings (San Antonio metro)",
      "On-call access during case-active windows",
      "After-hours availability for case-relevant events",
      "Travel-ready (quoted per trip)",
      "Full compliance oversight + records custody support",
      "Executive briefings for guardians, attorneys, providers",
    ],
  },
];

export type AlaCarteItem = {
  id: string;
  name: string;
  price: string;
  unit: string;
  notes?: string;
};

export const ALA_CARTE: AlaCarteItem[] = [
  { id: "strategy", name: "Strategy Session", price: "$297", unit: "60 min" },
  { id: "meeting", name: "ARD/IEP Meeting Representation", price: "$650", unit: "per meeting", notes: "Local, up to 3 hrs" },
  { id: "compliance", name: "Compliance / Records Review", price: "$450", unit: "flat" },
  { id: "presence", name: "Advocacy Presence & Support", price: "$175", unit: "per hour" },
  { id: "afterhours", name: "Emergency / After-Hours Support", price: "$275", unit: "per hour", notes: "2-hour minimum" },
  { id: "travel", name: "Travel", price: "IRS mileage + $85/hr", unit: "beyond 60 mi", notes: "From San Antonio" },
];

export const COMPASSION_PLAN = {
  name: "Accessible FSAP Compassion Plan",
  monthly: 99,
  priceId: "fsap_compassion_monthly",
  targetIncome: "≈ $888/mo (SSI-level)",
  purpose:
    "Designed to protect basic living needs while still providing meaningful advocacy for disabled adults and low-income families.",
  features: [
    "1 × 30-min strategy call per month",
    "2 advocacy hours per month",
    "Written meeting prep for one ARD/IEP or agency meeting per quarter",
    "1 ARD meeting representation per quarter at no additional fee",
    "Email lane, 3 business-day response",
  ],
  boundaries: [
    "Enrollment reviewed quarterly; documentation of income may be requested",
    "Additional hours billed at reduced rate ($85/hr) with prior approval",
    "Travel outside metro not included; may be waived case-by-case",
  ],
};

export const SLIDING_SCALE_TIERS = [
  { label: "25% reduction", income: "Household income at or below 300% FPL" },
  { label: "50% reduction", income: "Household income at or below 200% FPL" },
  { label: "75% reduction", income: "Household income at or below 150% FPL" },
];

export const SERVICE_STACK = [
  {
    name: "Full-Scope Advocacy Partner (FSAP)",
    summary: "Ongoing advocacy retainer with strategy, representation, and presence.",
  },
  {
    name: "ARD Strategy & Advocacy Specialist",
    summary: "Pre-meeting strategy, IEP goal shaping, and post-meeting follow-through.",
  },
  {
    name: "IEP/ARD Representation Specialist",
    summary: "Sit-in representation for ARD, IEP, MDR, and reevaluation meetings.",
  },
  {
    name: "Educational Advocacy & Compliance Specialist",
    summary: "IDEA/504 compliance reviews, records audits, corrective-action tracking.",
  },
  {
    name: "Advocacy Presence & Support Services",
    summary: "Accompaniment for agency, provider, and community meetings.",
  },
  {
    name: "Emergency / After-Hours Support",
    summary: "Case-relevant urgent windows (imminent ARD, hospitalization, escalation).",
  },
  {
    name: "Accessible FSAP Compassion Plan",
    summary: "Reduced-rate advocacy for SSI-level and low-income clients.",
  },
];

export const POSITIONING =
  "This is not basic consulting. This is strategic advocacy, real-world presence, and support when families need someone experienced in the room.";

export const CREDIBILITY =
  "Built on 12+ years of direct one-on-one advocacy and support experience, including long-term support of a quadriplegic adult with cerebral palsy across education, human services, and daily life systems — including a 15-year college journey through graduation.";

export const BOUNDARIES = [
  "FSAP provides advocacy and support — not legal advice, not medical advice, not therapy.",
  "No outcome guarantees. Advocacy improves preparation, presence, and process — not predetermined results.",
  "Mandated reporting applies. Suspected abuse, neglect, or exploitation will be reported per Texas law.",
  "Sliding scale, scholarships, and fee waivers are case-by-case, tied to documented need and available funding, and may be time-limited or scoped to specific services.",
];

export const CTA_LINE =
  "When the room matters, presence matters. Book a consultation to secure experienced advocacy and support.";

// --- Travel estimator ----------------------------------------------------
/**
 * Published travel rates used by the retainer travel estimator.
 * These mirror the written travel policy on /advocacy — estimates only,
 * every trip is still quoted and approved in writing before booking.
 */
export const TRAVEL_RATES = {
  /** Current IRS standard business mileage rate, $/mile. */
  mileagePerMile: 0.7,
  /** Miles included at no charge (round trip) before mileage applies. */
  includedMiles: 60,
  /** Windshield/travel time, $/hr, billed beyond the included radius. */
  travelHourly: 85,
  /** Average driving speed used to convert miles into billable travel hours. */
  avgDriveMph: 55,
  /** Round-trip airfare estimate for out-of-region engagements. */
  airfareRoundTrip: 550,
  /** Ground transport (rental/rideshare) per day when flying. */
  groundPerDay: 95,
  /** Lodging per night. Nights = days - 1, minimum 0. */
  lodgingPerNight: 185,
  /** Meals & incidentals per day. */
  perDiemPerDay: 79,
  /** On-site presence rate per day, by retainer tier. */
  onSitePerDay: {
    foundation: 0,
    priority: 650,
    elite: 1200,
  } as Record<string, number>,
} as const;

export type TravelMode = "local" | "drive" | "fly";

export const TRAVEL_MODES: { id: TravelMode; label: string; hint: string }[] = [
  { id: "local", label: "San Antonio metro", hint: "Within 30 miles — no travel billing" },
  { id: "drive", label: "Regional drive", hint: "Texas & adjoining states" },
  { id: "fly", label: "National (air travel)", hint: "Airfare, ground transport & lodging" },
];

export type TravelEstimateLine = { label: string; detail: string; amount: number };

export type TravelEstimateInput = {
  mode: TravelMode;
  /** One-way distance in miles (drive mode only). */
  oneWayMiles: number;
  /** Trip length in days, 1 = same-day. */
  days: number;
  /** Retainer tier id, drives the on-site day rate. */
  tierId: string;
};

/**
 * Pure calculator — no formatting, no rounding to currency. Returns line
 * items plus a total so the UI can render a transparent breakdown.
 */
export function estimateTravelCost({
  mode,
  oneWayMiles,
  days,
  tierId,
}: TravelEstimateInput): { lines: TravelEstimateLine[]; total: number } {
  const r = TRAVEL_RATES;
  const tripDays = Math.max(1, Math.round(days));
  const nights = Math.max(0, tripDays - 1);
  const lines: TravelEstimateLine[] = [];

  if (mode === "local") {
    return { lines, total: 0 };
  }

  if (mode === "drive") {
    const roundTrip = Math.max(0, oneWayMiles) * 2;
    const billableMiles = Math.max(0, roundTrip - r.includedMiles);
    lines.push({
      label: "Mileage",
      detail: `${billableMiles.toLocaleString()} billable mi (${roundTrip.toLocaleString()} round trip − ${r.includedMiles} included) × $${r.mileagePerMile.toFixed(2)}`,
      amount: billableMiles * r.mileagePerMile,
    });
    const travelHours = Math.round((billableMiles / r.avgDriveMph) * 10) / 10;
    lines.push({
      label: "Travel time",
      detail: `${travelHours} hr × $${r.travelHourly}/hr`,
      amount: travelHours * r.travelHourly,
    });
  }

  if (mode === "fly") {
    lines.push({
      label: "Airfare (estimate)",
      detail: "Round trip, booked at documented cost",
      amount: r.airfareRoundTrip,
    });
    lines.push({
      label: "Ground transport",
      detail: `${tripDays} day${tripDays === 1 ? "" : "s"} × $${r.groundPerDay}`,
      amount: tripDays * r.groundPerDay,
    });
  }

  if (nights > 0) {
    lines.push({
      label: "Lodging",
      detail: `${nights} night${nights === 1 ? "" : "s"} × $${r.lodgingPerNight}`,
      amount: nights * r.lodgingPerNight,
    });
  }

  lines.push({
    label: "Meals & incidentals",
    detail: `${tripDays} day${tripDays === 1 ? "" : "s"} × $${r.perDiemPerDay}`,
    amount: tripDays * r.perDiemPerDay,
  });

  const onSite = r.onSitePerDay[tierId] ?? 0;
  if (onSite > 0) {
    lines.push({
      label: "On-site presence rate",
      detail: `${tripDays} day${tripDays === 1 ? "" : "s"} × $${onSite.toLocaleString()}`,
      amount: tripDays * onSite,
    });
  }

  const total = lines.reduce((sum, l) => sum + l.amount, 0);
  return { lines, total };
}

// --- Tier deliverables checklist -----------------------------------------
/**
 * Concrete, checkable deliverables per retainer tier, grouped by phase.
 * Higher tiers inherit everything below them; `inheritsFrom` documents that.
 */
export type DeliverableGroup = { group: string; items: string[] };

export const TIER_DELIVERABLES: Record<
  PremiumTier["id"],
  { inheritsFrom?: string; groups: DeliverableGroup[] }
> = {
  foundation: {
    groups: [
      {
        group: "Onboarding (first 14 days)",
        items: [
          "Signed service agreement, scope, and boundaries in writing",
          "Intake call to map the case, systems involved, and desired outcomes",
          "Records intake checklist (IEP/504, evaluations, correspondence, discipline)",
          "Baseline advocacy plan with priorities for the first 90 days",
        ],
      },
      {
        group: "Every month",
        items: [
          "1 ARD/IEP or agency meeting attended (local, up to 3 hrs)",
          "2 × 30-minute strategy calls",
          "Written pre-meeting prep packet delivered 48 hrs before the meeting",
          "Written post-meeting recap with action items and owners",
          "Document review up to 15 pages",
          "Email lane with 2 business-day response",
        ],
      },
      {
        group: "Every quarter",
        items: [
          "Progress review against the advocacy plan",
          "Updated priorities and next-quarter meeting calendar",
        ],
      },
    ],
  },
  priority: {
    inheritsFrom: "Foundation FSAP",
    groups: [
      {
        group: "Onboarding (first 14 days)",
        items: [
          "Full records audit with compliance flags noted in writing",
          "Stakeholder map (school, agencies, providers, attorney if applicable)",
          "Escalation ladder documented, with triggers and timelines",
        ],
      },
      {
        group: "Every month",
        items: [
          "2 meetings attended (ARD/IEP, agency, or provider)",
          "Weekly 30-minute strategy call",
          "48-hour email and phone response window",
          "Compliance review with records tracking log updated",
          "Priority scheduling when an escalation window opens",
        ],
      },
      {
        group: "Every quarter",
        items: [
          "Written advocacy plan formally revised and re-issued",
          "Cross-agency coordination summary for your file",
        ],
      },
    ],
  },
  elite: {
    inheritsFrom: "Priority FSAP",
    groups: [
      {
        group: "Onboarding (first 14 days)",
        items: [
          "Case-active protocol defined, including after-hours contact rules",
          "Records custody support and secure document organization",
          "Travel readiness profile (approvals, quoting process, lead times)",
        ],
      },
      {
        group: "Ongoing",
        items: [
          "Unlimited local meetings across San Antonio metro",
          "On-call access during case-active windows",
          "After-hours availability for case-relevant events",
          "Full compliance oversight across every involved system",
          "Travel-ready engagement, quoted and approved per trip",
        ],
      },
      {
        group: "Every quarter",
        items: [
          "Executive briefing for guardians, attorneys, and providers",
          "Risk and outcome review with documented recommendations",
        ],
      },
    ],
  },
};

/** What is explicitly NOT included at any tier. Shown with the checklist. */
export const TIER_EXCLUSIONS = [
  "Legal representation, legal advice, or filing on your behalf",
  "Medical, psychiatric, or therapeutic treatment",
  "Guaranteed outcomes, placements, or funding decisions",
  "Travel outside the metro without a written, approved quote",
];
